import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { cn, formatTime } from '../lib/utils';
import { APP_VERSION } from '../config/appVersion';

export interface StatusBarProps {
  now: Date;
  liveness: { latency_ms?: number } | null;
  resources: {
    memory?: { processMb?: number };
    cpu?: { usagePercent?: number | null };
  } | null;
  systemHealthLoading: boolean;
  apiUnavailable: boolean;
  resourcesError: Error | null;
  agentRunning: boolean;
}

export const StatusBar: FC<StatusBarProps> = ({
  now,
  liveness,
  resources,
  systemHealthLoading,
  apiUnavailable,
  resourcesError,
  agentRunning,
}: StatusBarProps) => {
  const { t } = useTranslation(['layout', 'common']);
  void liveness;

  return (
    <footer className="h-8 border-t border-border bg-card/50 backdrop-blur-sm">
      <div className="h-full px-4 flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-4">
          <span>{t('layout:statusBar.time')}: {formatTime(now)}</span>
          {apiUnavailable && (
            <>
              <span>|</span>
              <span className="text-red-500">{t('layout:statusBar.apiUnavailable')}</span>
            </>
          )}
          <span>|</span>
          <span
            className={cn(
              resourcesError && !apiUnavailable && !resources?.memory?.processMb ? 'text-red-500' : ''
            )}
          >
            {t('layout:statusBar.memory')}:{' '}
            {systemHealthLoading && !resources
              ? t('common:states.loading')
              : resources?.memory?.processMb != null
                ? `${resources.memory.processMb}MB`
                : resourcesError && !apiUnavailable
                  ? t('common:states.error')
                  : '—'}
          </span>
          <span>|</span>
          <span
            className={cn(resourcesError && !apiUnavailable && resources?.cpu == null ? 'text-red-500' : '')}
          >
            {t('layout:statusBar.cpu')}:{' '}
            {systemHealthLoading && !resources
              ? t('common:states.loading')
              : resources?.cpu?.usagePercent != null
                ? `${Number(resources.cpu.usagePercent).toFixed(1)}%`
                : resourcesError && !apiUnavailable
                  ? t('common:states.error')
                  : '—'}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span>v{APP_VERSION}</span>
          <span>|</span>
          <span
            className={cn(
              agentRunning ? 'text-green-500' : 'text-muted-foreground'
            )}
          >
            {agentRunning ? t('layout:statusBar.agentRunning') : t('layout:statusBar.agentStopped')}
          </span>
        </div>
      </div>
    </footer>
  );
};
